/**
 * Help (Помощь) — FAQ + support.
 *
 * Tamagui port of the handoff `HelpScreen` (screen-misc.jsx), re-skinned to the
 * AS Life brand (navy/bronze instead of the handoff purple):
 *  • FAQ — accordion of frequently-asked questions, one open at a time, each
 *    tagged with a tone dot for its category
 *  • Support — "didn't find an answer?" card (life-buoy + hours + contact CTA),
 *    direct channels (mail / phone / Teams), and a shortcut into the KB
 *
 * The contact CTA and channel rows are wired to real `mailto:` / `tel:` / web
 * links via `Linking`, so they work without a dedicated support endpoint.
 * Responsive: FAQ + support sit side-by-side on wide screens, stack on phones.
 */
import { useState } from 'react'
import { Linking } from 'react-native'
import Constants from 'expo-constants'
import { useRouter } from 'expo-router'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { useResponsive } from '../../src/lib/responsive'
import { useFaq } from '../../src/features/help/useHelp'
import type { Faq } from '../../src/lib/types'
import type { Localized } from '../../src/i18n'
import { tone, type Tone } from '../../src/theme/tones'
import { Button, Card, Dot, Enter, Icon, PressCard, QueryState, Tag } from '../../src/ui'
import { Page } from '../../src/ui/Screen'
import { isComingSoon } from '../../src/app/nav'

const TERTIARY = '$tertiary'

interface SupportChannel { id: string; icon: string; label: Localized; value: string; href: string; tone: Tone; /** Channel isn't wired yet — the row renders dimmed + non-interactive with a «Скоро» pill. Drop the flag to re-enable. */ soon?: boolean }

/**
 * Каналы поддержки. Почта и телефон — РЕАЛЬНЫЕ контакты helpdesk, поэтому значения живут в
 * app.config.ts extra (EXPO_PUBLIC_SUPPORT_EMAIL/PHONE), а не в коде и не в src/demo: сменился
 * номер — правится env, а не файл. Teams ещё не подключён (soon) — там подставлять нечего.
 */
const supportChannels = (): SupportChannel[] => {
  const extra = Constants.expoConfig?.extra ?? {}
  const email = (extra.supportEmail as string) ?? ''
  const phone = (extra.supportPhone as string) ?? ''
  const all: SupportChannel[] = [
    { id: 'mail', icon: 'mail', label: { ru: 'Электронная почта', en: 'Email' }, value: email, href: `mailto:${email}`, tone: 'blue' },
    { id: 'phone', icon: 'phone', label: { ru: 'Телефон поддержки', en: 'Support line' }, value: phone, href: `tel:${phone.replace(/[^\d+]/g, '')}`, tone: 'green' },
    { id: 'chat', icon: 'message-circle', label: { ru: 'Чат в Microsoft Teams', en: 'Microsoft Teams chat' }, value: 'AS Group · IT Helpdesk', href: 'https://teams.microsoft.com', tone: 'purple', soon: true },
  ]
  // Канал без настроенного значения не рисуем пустой кликабельной строкой.
  return all.filter((c) => c.value)
}

// ── FAQ accordion row ─────────────────────────────────────────────────────────
function FaqRow({ item, open, last, onToggle }: { item: Faq; open: boolean; last: boolean; onToggle: () => void }) {
  const { t } = useApp()
  return (
    <YStack {...(last ? null : { borderBottomWidth: 1, borderColor: '$borderSecondary' })}>
      <XStack
        onPress={onToggle}
        cursor="pointer"
        alignItems="center"
        gap={12}
        paddingVertical={15}
        paddingHorizontal={16}
        hoverStyle={{ backgroundColor: '$backgroundHover' }}
      >
        <Dot tone={item.tone} size={8} />
        <Text flex={1} fontSize={14} fontWeight="600" color="$color" lineHeight={20}>
          {t(item.q)}
        </Text>
        <Stack animation="quick" rotate={open ? '180deg' : '0deg'}>
          <Icon name="chevron-down" size={18} color={TERTIARY} />
        </Stack>
      </XStack>
      {open && (
        <YStack
          paddingBottom={16}
          paddingHorizontal={36}
          gap={6}
          animation="quicker"
          enterStyle={{ opacity: 0, y: -6 }}
          opacity={1}
          y={0}
        >
          <Text fontSize={13} color="$muted" lineHeight={20}>
            {t(item.a)}
          </Text>
          <Text fontSize={11} fontWeight="700" color={tone(item.tone).text}>
            {t(item.cat)}
          </Text>
        </YStack>
      )}
    </YStack>
  )
}

// ── FAQ empty state ───────────────────────────────────────────────────────────
/**
 * Пока вопросов нет — не строчка «Пока нет вопросов», а заполненная карточка на всю высоту
 * колонки (владелец, 29.10.2026): крупный «?» в круге, искорки вокруг и подпись, что раздел
 * пополняется по мере поступления частых вопросов.
 */
function FaqEmpty() {
  const { l } = useApp()
  const gold = tone('gold').solid
  const purple = tone('purple').solid
  return (
    <YStack flex={1} alignItems="center" justifyContent="center" gap={14} paddingHorizontal={28} paddingVertical={36}>
      <Stack width={132} height={112} alignItems="center" justifyContent="center">
        <Stack width={88} height={88} borderRadius={999} backgroundColor="$primaryBg" alignItems="center" justifyContent="center">
          <Icon name="circle-help" size={44} color="$primary" strokeWidth={1.75} />
        </Stack>
        {/* искорки — разного размера и тона, вразброс вокруг круга */}
        <Stack position="absolute" top={2} right={10}>
          <Icon name="sparkles" size={22} color={gold} />
        </Stack>
        <Stack position="absolute" bottom={8} left={6}>
          <Icon name="sparkles" size={16} color={purple} />
        </Stack>
        <Stack position="absolute" top={26} left={0} opacity={0.7}>
          <Icon name="sparkles" size={11} color={gold} />
        </Stack>
      </Stack>
      <Text fontSize={16} fontWeight="700" color="$color" textAlign="center">
        {l('help.faqEmpty')}
      </Text>
      <Text fontSize={13} color="$muted" lineHeight={20} textAlign="center" maxWidth={380}>
        {l('help.faqEmptyHint')}
      </Text>
    </YStack>
  )
}

// ── FAQ card ──────────────────────────────────────────────────────────────────
function Faqs() {
  const { l } = useApp()
  // Live /help/faq only — an empty or unreachable response shows the empty state.
  const faqQuery = useFaq()
  const items: Faq[] = faqQuery.data ?? []
  // null → default (first open); '' → all collapsed; otherwise the open id.
  const [openId, setOpenId] = useState<string | null>(null)
  const activeId = openId ?? items[0]?.id ?? null

  return (
    <YStack flex={1} gap={12}>
      <QueryState q={faqQuery}>
      <Card flex={1} overflow="hidden">
      <Text fontSize={14} fontWeight="700" color="$color" paddingHorizontal={16} paddingTop={16} paddingBottom={6}>
        {l('help.faqTitle')}
      </Text>
      {items.length === 0 ? (
        <FaqEmpty />
      ) : (
        items.map((f, i) => (
          // mount cascade only — the accordion open/close animation lives in FaqRow
          <Enter key={f.id} index={Math.min(i, 11)}>
            <FaqRow
              item={f}
              open={activeId === f.id}
              last={i === items.length - 1}
              onToggle={() => setOpenId(activeId === f.id ? '' : f.id)}
            />
          </Enter>
        ))
      )}
      </Card>
      </QueryState>
    </YStack>
  )
}

// ── Support panel ──────────────────────────────────────────────────────────────
function Support() {
  const { l, t } = useApp()
  const router = useRouter()
  const supportUrl = (Constants.expoConfig?.extra?.supportUrl as string) ?? ''

  return (
    <YStack gap={16}>
      {/* "Didn't find an answer?" + contact CTA */}
      <Enter index={0}>
      <Card padding={20} gap={16}>
        <XStack gap={14} alignItems="center">
          <Stack
            width={44}
            height={44}
            borderRadius={9}
            backgroundColor="$primaryBg"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="life-buoy" size={22} color="$primary" />
          </Stack>
          <YStack flex={1} minWidth={0}>
            <Text fontSize={14} fontWeight="700" color="$color">
              {l('help.supportTitle')}
            </Text>
            <Text fontSize={12} color={TERTIARY} marginTop={2} lineHeight={16}>
              {l('help.supportHours')}
            </Text>
          </YStack>
        </XStack>
        {/* «Написать в поддержку» — opens the support portal (URL from app.config extra). */}
        <Button
          variant="primary"
          block
          onPress={() => Linking.openURL(supportUrl)}
          icon={<Icon name="send" size={15} color="$onPrimary" />}
        >
          {l('help.contact')}
        </Button>
      </Card>
      </Enter>

      {/* Direct channels */}
      <Enter index={1}>
      <Card paddingHorizontal={20} paddingTop={16} paddingBottom={8}>
        <Text fontSize={13} fontWeight="700" color="$color" marginBottom={4}>
          {l('help.channels')}
        </Text>
        {supportChannels().map((c, i) => {
          const tn = tone(c.tone)
          // Not-ready channels (e.g. Teams) render dimmed + non-interactive with a «Скоро» pill.
          const soon = !!c.soon
          return (
            <XStack
              key={c.id}
              {...(soon ? null : { onPress: () => Linking.openURL(c.href) })}
              cursor={soon ? 'default' : 'pointer'}
              opacity={soon ? 0.55 : 1}
              gap={12}
              alignItems="center"
              paddingVertical={11}
              {...(soon ? null : { hoverStyle: { opacity: 0.85 } })}
              {...(i ? { borderTopWidth: 1, borderColor: '$borderSecondary' } : null)}
            >
              <Stack
                width={34}
                height={34}
                borderRadius={8}
                backgroundColor={tn.soft}
                alignItems="center"
                justifyContent="center"
              >
                <Icon name={c.icon} size={16} color={tn.text} />
              </Stack>
              <YStack flex={1} minWidth={0}>
                <Text fontSize={11} color={TERTIARY}>
                  {t(c.label)}
                </Text>
                <Text fontSize={13} fontWeight="600" color="$color">
                  {c.value}
                </Text>
              </YStack>
              {soon ? (
                <Tag tone="default">{l('c.soon')}</Tag>
              ) : (
                <Icon name="external-link" size={14} color={TERTIARY} />
              )}
            </XStack>
          )
        })}
      </Card>
      </Enter>

      {/* KB shortcut — hidden while «База знаний» is a not-ready section */}
      {!isComingSoon('/kb') && (
        <Enter index={2}>
        <PressCard onPress={() => router.push('/kb')} padding={16} flexDirection="row" alignItems="center" gap={12}>
          <Stack
            width={34}
            height={34}
            borderRadius={8}
            backgroundColor={tone('gold').soft}
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="book-marked" size={16} color={tone('gold').text} />
          </Stack>
          <Text flex={1} fontSize={13} fontWeight="600" color="$color">
            {l('help.kbLink')}
          </Text>
          <Icon name="chevron-right" size={16} color={TERTIARY} />
        </PressCard>
        </Enter>
      )}
    </YStack>
  )
}

export default function HelpScreen() {
  const { l } = useApp()
  const { isPhone } = useResponsive()

  return (
    <Page title={l('help.title')} subtitle={l('help.subtitle')} maxWidth={1000}>
      {/* stretch: «Частые вопросы» дотягиваются до низа правой колонки (до «Все статьи в Базе знаний») */}
      <XStack gap={16} flexDirection={isPhone ? 'column' : 'row'} alignItems="stretch">
        <YStack flex={1} minWidth={0} width={isPhone ? '100%' : undefined}>
          <Faqs />
        </YStack>
        <YStack width={isPhone ? '100%' : 340} flexShrink={0}>
          <Support />
        </YStack>
      </XStack>
      <Stack height={8} />
    </Page>
  )
}
